import { CheckIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';

interface StyledCheckButtonProps {
  onClick?: () => void;
  onClickAsync?: () => Promise<void>;

  type: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  rootClassName?: string;
  className?: string;
  tooltip?: string;
  isChecked?: boolean;

  children?: React.ReactNode;
}

export function StyledCheckButton(props: StyledCheckButtonProps) {
  const { type, isChecked, onClick, onClickAsync, children, disabled } = props;

  const [isClicking, setIsClicking] = useState<boolean>(false);

  const onClickInvoke = useCallback(async () => {
    if (onClick) {
      return onClick();
    }

    if (onClickAsync) {
      try {
        setIsClicking(true);

        await onClickAsync();
      } finally {
        setIsClicking(false);
      }
    }
  }, [onClick, onClickAsync]);

  const [tooltipId] = useState<string>(uuidv4());

  return (
    <div className={classNames(props.rootClassName && props.rootClassName)}>
      {props.tooltip && <Tooltip id={tooltipId}></Tooltip>}

      <button
        type={type}
        onClick={onClickInvoke}
        disabled={isClicking || disabled}
        data-tooltip-id={tooltipId}
        data-tooltip-content={props.tooltip}
        className={classNames(
          'flex rounded-lg border text-sm font-light px-4 py-2 shadow-xs select-none', //frame + alignments
          'disabled:opacity-50 gap-2 items-center w-full', //effects

          'outline-offset-1 ring-offset-transparent ring-offset-1 ring-1', //set outline-2 to show outline, always set color

          'bg-brand-cashmere-100  ring-transparent outline-brand-pink text-black',
          !disabled && 'focus:ring-brand-pink hover:ring-brand-pink',

          isChecked && 'bg-brand-pink text-white',

          props.className && props.className
        )}
      >
        <div className="border border-gray-600 bg-white size-4 flex items-center justify-center flex-shrink-0">
          {isChecked && (
            <CheckIcon className="size-4 text-brand-pink"></CheckIcon>
          )}
        </div>

        {children}
      </button>
    </div>
  );
}

export default StyledCheckButton;
